// NOMBASE canonical confirm dialog — one UI for every destructive,
// warning, and brand-primary action that needs a yes/no gate.
//
// Replaces:
//   • Every native `window.confirm()` in the app (destructive or otherwise)
//   • Every ad-hoc `<Dialog>` / `<AlertDialog>` scattered across feature pages
//
// Design language mirrors the "Add Supplier" drawer family:
//   - rounded-2xl content
//   - rounded-xl action buttons
//   - rose destructive accent (default), amber warning, slate brand-primary
//   - bolded item name inside the description
//   - colored circular icon for extra weight
//
// Three usage modes:
//   1. Controlled <ConfirmDeleteDialog/> — destructive (default), legacy name kept
//      for backward compatibility.
//   2. Controlled <ConfirmDialog variant="warning|primary|destructive"/> — all three.
//   3. Imperative: `useDeleteConfirm()` (destructive-only, simple api) OR
//      `useConfirm()` (supports `variant`).
import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlertTriangle, Loader2, Send, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';

// ─────────────────────────────────────────────────────────────
// Variant tokens
// ─────────────────────────────────────────────────────────────

const VARIANTS = {
  destructive: {
    iconBg: 'bg-rose-50',
    iconColor: 'text-rose-600',
    Icon: AlertTriangle,
    actionClass: 'bg-rose-600 hover:bg-rose-700',
    defaultTitle: 'Delete item?',
    defaultConfirm: 'Delete',
  },
  warning: {
    iconBg: 'bg-amber-50',
    iconColor: 'text-amber-600',
    Icon: AlertTriangle,
    actionClass: 'bg-amber-600 hover:bg-amber-700',
    defaultTitle: 'Are you sure?',
    defaultConfirm: 'Continue',
  },
  override: {
    // Informed override — the server said "this needs a human decision"
    // (unqualified supplier, spec deviation, restricted substance). Amber,
    // never destructive-red: the action is allowed, just consequential.
    iconBg: 'bg-amber-50',
    iconColor: 'text-amber-600',
    Icon: AlertTriangle,
    actionClass: 'bg-amber-600 hover:bg-amber-700',
    defaultTitle: 'Proceed anyway?',
    defaultConfirm: 'Proceed anyway',
  },
  primary: {
    iconBg: 'bg-slate-900',
    iconColor: 'text-white',
    Icon: Send,
    actionClass: 'bg-slate-900 hover:bg-slate-800',
    defaultTitle: 'Confirm action',
    defaultConfirm: 'Confirm',
  },
  success: {
    iconBg: 'bg-emerald-50',
    iconColor: 'text-emerald-600',
    Icon: CheckCircle2,
    actionClass: 'bg-emerald-600 hover:bg-emerald-700',
    defaultTitle: 'Are you ready?',
    defaultConfirm: 'Confirm',
  },
};

// ─────────────────────────────────────────────────────────────
// Controlled component
// ─────────────────────────────────────────────────────────────

export function ConfirmDialog({
  open,
  onOpenChange,
  onConfirm,
  variant = 'destructive',
  title,
  itemName,                 // rendered bold inline inside the auto description
  description,              // full custom sentence (overrides the auto one)
  consequence,              // short trailing line below description
  confirmLabel,
  cancelLabel = 'Cancel',
  busy = false,
  icon = true,
  requireReason = false,    // textarea; confirm disabled until non-empty; onConfirm(reason)
  testId = 'confirm-dialog',
}) {
  const [reason, setReason] = useState('');
  React.useEffect(() => { if (!open) setReason(''); }, [open]);
  const v = VARIANTS[variant] || VARIANTS.destructive;
  const Icon = v.Icon;
  const resolvedTitle = title || v.defaultTitle;
  const resolvedConfirmLabel = confirmLabel || v.defaultConfirm;

  // See note in handleAction: onConfirm runs while we suppress the close
  // so async work can settle; caller drives `busy`/`onOpenChange`.
  const handleAction = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    if (requireReason && !reason.trim()) return;
    await onConfirm?.(requireReason ? reason.trim() : undefined);
  };

  const body = description || (
    variant === 'destructive' ? (
      <>
        Are you sure you want to delete
        {itemName ? <> <strong className="text-slate-900 font-semibold">&quot;{itemName}&quot;</strong></> : ' this item'}
        ? This action cannot be undone.
      </>
    ) : (
      <>
        {itemName ? <>Continue with <strong className="text-slate-900 font-semibold">&quot;{itemName}&quot;</strong>?</> : 'Continue with this action?'}
      </>
    )
  );

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="rounded-2xl max-w-md" data-testid={testId}>
        <AlertDialogHeader>
          <div className="flex items-start gap-3">
            {icon && (
              <div className={cn('w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0', v.iconBg)}>
                <Icon className={cn('w-4 h-4', v.iconColor)} strokeWidth={2.2} />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <AlertDialogTitle className="text-[16px] font-bold text-slate-900 leading-tight">
                {resolvedTitle}
              </AlertDialogTitle>
              <AlertDialogDescription className="text-[13px] text-slate-600 leading-relaxed mt-1.5">
                {body}
                {consequence && (
                  <span className="block mt-1.5 text-[12px] text-slate-500">{consequence}</span>
                )}
              </AlertDialogDescription>
            </div>
          </div>
        </AlertDialogHeader>
        {requireReason && (
          <textarea
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (required — recorded in the audit trail)"
            className="w-full rounded-xl border border-gray-200 text-sm p-2.5 focus:outline-none focus:ring-2 focus:ring-amber-200"
            data-testid={`${testId}-reason`}
          />
        )}
        <AlertDialogFooter className="gap-2 sm:gap-2">
          <AlertDialogCancel
            className="rounded-xl h-10 px-4 text-[12px] font-semibold"
            data-testid={`${testId}-cancel`}
            disabled={busy}
          >
            {cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleAction}
            disabled={busy || (requireReason && !reason.trim())}
            className={cn(
              'rounded-xl h-10 px-4 text-[12px] font-semibold text-white inline-flex items-center gap-1.5',
              v.actionClass,
              busy && 'opacity-60 cursor-wait',
            )}
            data-testid={`${testId}-action`}
          >
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {resolvedConfirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// Legacy alias — existing call-sites pass the exact same props shape
// since `variant` defaults to 'destructive'.
export const ConfirmDeleteDialog = ConfirmDialog;

// ─────────────────────────────────────────────────────────────
// Imperative hook — drop-in replacement for `window.confirm`
//
// Mount <ConfirmDeleteProvider/> once near the app root, then:
//
//   const confirm = useConfirm();                      // full api
//   const confirmDelete = useDeleteConfirm();          // destructive shortcut
//
//   const ok = await confirm({
//     variant: 'warning',
//     title: 'Cancel review?',
//     description: '3 items will be discarded.',
//     confirmLabel: 'Cancel review',
//   });
//   if (!ok) return;
// ─────────────────────────────────────────────────────────────

const DeleteCtx = createContext(null);

export function ConfirmDeleteProvider({ children }) {
  const [state, setState] = useState(null); // props for dialog when active
  const resolverRef = useRef(null);

  const show = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({ ...opts });
    });
  }, []);

  const close = (value) => {
    resolverRef.current?.(value);
    resolverRef.current = null;
    setState(null);
  };

  return (
    <DeleteCtx.Provider value={show}>
      {children}
      {state && (
        <ConfirmDialog
          open
          onOpenChange={(v) => { if (!v) close(false); }}
          onConfirm={() => close(true)}
          {...state}
        />
      )}
    </DeleteCtx.Provider>
  );
}

// Generic variant-aware version. Use this for Publish/Archive/Send-PO flows.
export function useConfirm() {
  const ctx = useContext(DeleteCtx);
  if (!ctx) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('[useConfirm] <ConfirmDeleteProvider/> not mounted — falling back to window.confirm.');
    }
    return async (opts = {}) => window.confirm(
      `${opts.title || 'Are you sure?'}\n\n${
        typeof opts.description === 'string'
          ? opts.description
          : (opts.itemName ? `Continue with "${opts.itemName}"?` : 'Continue with this action?')
      }`
    );
  }
  return ctx;
}

// Destructive shortcut — forces variant='destructive' so existing call-sites
// keep their current contract (no accidental style drift).
export function useDeleteConfirm() {
  const confirm = useConfirm();
  return (opts = {}) => confirm({ ...opts, variant: 'destructive' });
}
